import { onAuthStateChanged, signInWithEmailAndPassword, signOut, type User } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { auth, db } from "@/lib/firebase";
import type { Role } from "./roles";

/**
 * `loading`         — Firebase Auth або `admins/{uid}` doc ще завантажується
 * `unauthenticated` — користувач не залогінений
 * `no_access`       — залогінений, але немає документа в admins/{uid}
 * `authenticated`   — залогінений + є запис у admins/ з валідною (або дефолтною) роллю
 */
export type AuthStatus = "loading" | "authenticated" | "unauthenticated" | "no_access";

type AuthContextValue = {
  status: AuthStatus;
  user: User | null;
  role: Role | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOutUser: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

function parseRole(data: Record<string, unknown> | undefined): Role {
  // Backward compat: якщо поле `role` відсутнє — трактуємо як `owner`.
  const raw = data?.role;
  if (raw === "owner" || raw === "ops" || raw === "accountant") return raw;
  return "owner";
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [role, setRole] = useState<Role | null>(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (nextUser) => {
      setUser(nextUser);
      if (!nextUser) {
        setStatus("unauthenticated");
        setRole(null);
      } else {
        // Переходимо в loading, доки не зрезолвимо admins/{uid}.
        setStatus("loading");
      }
    });
    return () => unsub();
  }, []);

  // Підписуємось на admins/{uid} — live revoke працює одразу.
  useEffect(() => {
    if (!user) return;
    const ref = doc(db, "admins", user.uid);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setRole(null);
          setStatus("no_access");
          return;
        }
        setRole(parseRole(snap.data()));
        setStatus("authenticated");
      },
      (err) => {
        // Якщо rules не пускають (user != uid), вважаємо no_access.
        console.warn("[AuthProvider] admins/{uid} read error", err);
        setRole(null);
        setStatus("no_access");
      },
    );
    return () => unsub();
  }, [user]);

  const signIn = useCallback(async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  }, []);

  const signOutUser = useCallback(async () => {
    await signOut(auth);
  }, []);

  return (
    <AuthContext.Provider value={{ status, user, role, signIn, signOutUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

/**
 * Перекласти Firebase Auth-помилки в користувацькі повідомлення (uk).
 */
export function translateAuthError(code: string | undefined): string {
  const map: Record<string, string> = {
    "auth/invalid-email": "Некоректний email.",
    "auth/missing-password": "Введи пароль.",
    "auth/invalid-credential": "Невірний email або пароль.",
    "auth/user-not-found": "Користувача не існує.",
    "auth/wrong-password": "Невірний пароль.",
    "auth/too-many-requests": "Забагато спроб. Спробуй пізніше.",
  };
  return (code && map[code]) || "Помилка авторизації.";
}
